import {
  ActiveModeSchema,
  MapArtifactSchema,
  SUPPORTED_ARTIFACT_VERSIONS,
  artifactHashPayload,
  type CatalogEntry,
  type MapArtifact,
} from "@urlcade/contracts";
import { config, endpoint } from "./config";

const maxLegacyManifestBytes = 500_000;
const maxV4ManifestBytes = 1_000_000;
const maxLegacyMapAssetBytes = 8 * 1024 * 1024;
const maxV4MapAssetBytes = 16_000_000;

export interface LoadedMap {
  entry: CatalogEntry;
  artifact: MapArtifact;
  manifestUrl: string;
  assetUrls: ReadonlyMap<string, string>;
  dispose(): void;
}

export type MapLoadErrorCode = "CATALOG_UNAVAILABLE" | "MANIFEST_MISMATCH" | "VERSION_MISMATCH" | "ASSET_MISSING" | "ASSET_DIGEST_MISMATCH" | "ASSET_TOO_LARGE" | "ASSET_INVALID" | "CRYPTO_UNAVAILABLE";

export class MapLoadError extends Error {
  constructor(readonly code: MapLoadErrorCode, message: string = code) {
    super(message);
    this.name = "MapLoadError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCatalogEntry(value: unknown): value is CatalogEntry {
  if (!isRecord(value)) return false;
  return typeof value.mapId === "string" && typeof value.artifactHash === "string" && /^[a-f0-9]{64}$/.test(value.artifactHash) &&
    typeof value.title === "string" && value.title.length > 0 && ActiveModeSchema.safeParse(value.mode).success &&
    typeof value.manifestUrl === "string" && value.manifestUrl.length <= 240 &&
    typeof value.simulationVersion === "string" && typeof value.protocolVersion === "string";
}

function entriesFrom(value: unknown, baseUrl: string): CatalogEntry[] {
  const rows = Array.isArray(value) ? value : isRecord(value) && Array.isArray(value.maps) ? value.maps : null;
  if (!rows) throw new MapLoadError("CATALOG_UNAVAILABLE", "Katalog biçimi okunamadı.");
  const entries = rows.filter(isCatalogEntry);
  if (entries.length !== rows.length) throw new MapLoadError("CATALOG_UNAVAILABLE", "Katalogda geçersiz bir harita kaydı var.");
  if (new Set(entries.map((entry) => entry.artifactHash)).size !== entries.length) throw new MapLoadError("CATALOG_UNAVAILABLE", "Katalogda yinelenen harita sürümü var.");
  try {
    const base = new URL(baseUrl);
    return entries.map((entry) => {
      const url = new URL(entry.manifestUrl, base);
      if (url.origin !== base.origin || url.pathname !== `/maps/${entry.artifactHash}/manifest.json`) {
        throw new MapLoadError("CATALOG_UNAVAILABLE", "Katalog haritayı beklenen paket yolunda göstermiyor.");
      }
      return { ...entry, manifestUrl: url.toString() };
    });
  } catch {
    throw new MapLoadError("CATALOG_UNAVAILABLE", "Katalogdaki harita adresi geçersiz.");
  }
}

export async function loadCatalog(): Promise<CatalogEntry[]> {
  let serverFailure: unknown;
  let serverResponded = false;
  if (config.gameServerUrl) {
    try {
      const response = await fetch(endpoint("/api/maps"), { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(7000) });
      if (!response.ok) throw new Error(`HTTP_${response.status}`);
      serverResponded = true;
      const catalog = entriesFrom(await response.json(), config.gameServerUrl);
      if (catalog.length > 0) return catalog;
      serverFailure = new Error("NO_SERVER_MAPS");
    } catch (error) {
      serverFailure = error;
    }
  }

  try {
    const response = await fetch("/maps/catalog.json", { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(7000) });
    if (!response.ok) throw new Error(`HTTP_${response.status}`);
    const catalog = entriesFrom(await response.json(), window.location.origin);
    return catalog;
  } catch (error) {
    if (serverResponded) return [];
    if (serverFailure || error) throw new MapLoadError("CATALOG_UNAVAILABLE", "Harita listesi açılamadı.");
    return [];
  }
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (isRecord(value)) return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(",")}}`;
  return JSON.stringify(value) ?? "null";
}

async function sha256(bytes: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new MapLoadError("CRYPTO_UNAVAILABLE", "Güvenli harita özeti için Web Crypto gerekir.");
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function fetchLimited(url: string, limit: number, code: MapLoadErrorCode, signal?: AbortSignal): Promise<ArrayBuffer> {
  let response: Response;
  try {
    response = await fetch(url, { headers: { Accept: "application/json, image/*" }, signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(12_000)]) : AbortSignal.timeout(12_000) });
  } catch {
    throw new MapLoadError(code, code === "ASSET_MISSING" ? "Harita görseline erişilemedi." : "Harita dosyasına erişilemedi.");
  }
  if (!response.ok) throw new MapLoadError(code, `Harita dosyası açılamadı (HTTP ${response.status}).`);
  const advertisedSize = Number(response.headers.get("content-length"));
  if (Number.isFinite(advertisedSize) && advertisedSize > limit) throw new MapLoadError("ASSET_TOO_LARGE", "Harita dosyası izin verilen boyutu aşıyor.");
  const bytes = await response.arrayBuffer();
  if (bytes.byteLength > limit) throw new MapLoadError("ASSET_TOO_LARGE", "Harita dosyası izin verilen boyutu aşıyor.");
  return bytes;
}

export async function loadMap(entry: CatalogEntry, signal?: AbortSignal): Promise<LoadedMap> {
  let manifestUrl: URL;
  try {
    manifestUrl = new URL(entry.manifestUrl, window.location.origin);
  } catch {
    throw new MapLoadError("MANIFEST_MISMATCH", "Harita adresi geçersiz.");
  }
  if (!/^https?:$/.test(manifestUrl.protocol)) throw new MapLoadError("MANIFEST_MISMATCH", "Harita adresine izin verilmiyor.");

  const manifestBytes = await fetchLimited(manifestUrl.toString(), maxV4ManifestBytes, "MANIFEST_MISMATCH", signal);
  let raw: unknown;
  try { raw = JSON.parse(new TextDecoder().decode(manifestBytes)); }
  catch { throw new MapLoadError("MANIFEST_MISMATCH", "Harita dosyası geçerli JSON değil."); }
  const parsed = MapArtifactSchema.safeParse(raw);
  if (!parsed.success) throw new MapLoadError("MANIFEST_MISMATCH", "Harita dosyası sözleşmeye uymuyor.");
  const artifact = parsed.data;
  if (artifact.schemaVersion !== 4 && manifestBytes.byteLength > maxLegacyManifestBytes) throw new MapLoadError("MANIFEST_MISMATCH", "Harita manifesti izin verilen boyutu aşıyor.");

  if (artifact.artifactHash !== entry.artifactHash || artifact.mapId !== entry.mapId || artifact.setup.mode !== entry.mode) {
    throw new MapLoadError("MANIFEST_MISMATCH", "Harita kimliği ve sürümü katalogla uyuşmuyor.");
  }
  const versionsSupported = SUPPORTED_ARTIFACT_VERSIONS as readonly string[];
  const compilerSupported = artifact.schemaVersion === 4
    ? artifact.compilerVersion === "4.0.0"
    : versionsSupported.includes(artifact.compilerVersion);
  if (!versionsSupported.includes(artifact.protocolVersion) || !versionsSupported.includes(entry.protocolVersion) ||
    !versionsSupported.includes(artifact.simulationVersion) || !versionsSupported.includes(entry.simulationVersion) ||
    !compilerSupported || artifact.protocolVersion !== entry.protocolVersion ||
    artifact.simulationVersion !== entry.simulationVersion) {
    throw new MapLoadError("VERSION_MISMATCH", "Bu harita sürümü bu istemciyle uyumlu değil.");
  }

  const encodedManifest = new TextEncoder().encode(canonical(artifactHashPayload(artifact)));
  const hashBytes = encodedManifest.buffer.slice(encodedManifest.byteOffset, encodedManifest.byteOffset + encodedManifest.byteLength) as ArrayBuffer;
  const calculatedHash = await sha256(hashBytes);
  if (calculatedHash !== artifact.artifactHash) throw new MapLoadError("MANIFEST_MISMATCH", "Harita içerik özeti eşleşmiyor.");

  const assetBase = new URL(".", manifestUrl);
  const assetUrls = new Map<string, string>();
  const createdUrls: string[] = [];
  const maxAssetsBytes = artifact.schemaVersion === 4 ? maxV4MapAssetBytes : maxLegacyMapAssetBytes;
  let totalBytes = 0;
  try {
    for (let start = 0; start < artifact.assets.length; start += 4) {
      const batch = artifact.assets.slice(start, start + 4);
      const results = await Promise.all(batch.map(async (asset) => {
        const assetUrl = new URL(asset.path, assetBase);
        if (assetUrl.origin !== assetBase.origin || !assetUrl.pathname.startsWith(assetBase.pathname)) {
          throw new MapLoadError("MANIFEST_MISMATCH", "Harita varlığı kendi paketinin dışına çıkıyor.");
        }
        const bytes = await fetchLimited(assetUrl.toString(), maxAssetsBytes, "ASSET_MISSING", signal);
        totalBytes += bytes.byteLength;
        if (totalBytes > maxAssetsBytes) throw new MapLoadError("ASSET_TOO_LARGE", artifact.schemaVersion === 4 ? "Harita paketi 16 MB sınırını aşıyor." : "Harita paketi 8 MB sınırını aşıyor.");
        if (await sha256(bytes) !== asset.digest) throw new MapLoadError("ASSET_DIGEST_MISMATCH", "Harita görselinin özeti eşleşmiyor.");
        const blob = new Blob([bytes], { type: mimeForPath(asset.path) });
        if (typeof createImageBitmap === "function") {
          try {
            const bitmap = await createImageBitmap(blob);
            if (bitmap.width < 1 || bitmap.height < 1 || bitmap.width * bitmap.height > 32_000_000) {
              bitmap.close();
              throw new Error("INVALID_DIMENSIONS");
            }
            bitmap.close();
          } catch {
            throw new MapLoadError("ASSET_INVALID", "Harita görseli açılamadı.");
          }
        }
        const objectUrl = URL.createObjectURL(blob);
        createdUrls.push(objectUrl);
        return [asset.id, objectUrl] as const;
      }));
      for (const [id, url] of results) assetUrls.set(id, url);
    }
  } catch (error) {
    createdUrls.forEach(URL.revokeObjectURL);
    throw error;
  }

  return {
    entry,
    artifact,
    manifestUrl: manifestUrl.toString(),
    assetUrls,
    dispose: () => createdUrls.forEach(URL.revokeObjectURL),
  };
}

function mimeForPath(path: string): string {
  const suffix = path.toLowerCase().split(".").pop();
  return suffix === "png" ? "image/png" : suffix === "webp" ? "image/webp" : suffix === "svg" ? "image/svg+xml" : "image/jpeg";
}
